/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import moment from 'moment'

import { EuiCallOut } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useSessionStorageBackedState } from '@modules/utils/hooks/useLocalStorage'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import type { StackDeployment } from '@modules/ui-types'
import { isMonthly, isTrial } from '@modules/billing-lib/billingDetails/utils'

import { isDeploymentVersionBelow8dot10 } from './utils'

import type { FC, ReactNode } from 'react'

type CalloutContent =
  | {
      title: ReactNode
      description: ReactNode
    }
  | undefined

const CustomerEngineerCallout: FC<{ deployment: StackDeployment }> = ({ deployment }) => {
  const {
    data: billingData,
    loading: billingDataLoading,
    error: billingDataError,
  } = useBillingDetails()

  const [isCalloutVisible, setCalloutVisible] = useSessionStorageBackedState(
    `customerEngineerCalloutVisible`,
    true,
  )

  if (billingDataError || billingDataLoading || !billingData) {
    return null
  }

  const isTrialUser = isTrial(billingData)
  const isMonthlyUser = isMonthly(billingData)

  if (!isTrialUser && !isMonthlyUser) {
    return null
  }

  const billingDataTrial = billingData.trial
  const isConvertedUser = billingDataTrial.state === `converted`
  const daysSinceTrialConverted =
    isConvertedUser && billingDataTrial.end
      ? moment().diff(moment(billingDataTrial.end), 'days')
      : undefined

  const getCalloutContent = (): CalloutContent => {
    if (daysSinceTrialConverted) {
      if (daysSinceTrialConverted >= 60) {
        if (isDeploymentVersionBelow8dot10(deployment)) {
          return {
            title: (
              <FormattedMessage
                id='deployment-overview.customer-engineer-callout-title.version-below-8dot10'
                defaultMessage='Need help upgrading? Customer Engineering can help.'
              />
            ),
            description: (
              <FormattedMessage
                id='deployment-overview.customer-engineer-callout-description.version-below-8dot10'
                defaultMessage='<link> Connect with us </link> for expert guidance on navigating breaking changes, upgrade paths, and compatibility questions at no additional cost to you!'
                values={{
                  link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
                }}
              />
            ),
          }
        }

        return {
          title: (
            <FormattedMessage
              id='deployment-overview.customer-engineer-callout-title.conversion-gt-60'
              defaultMessage='Need advice? Engage Customer Engineering.'
            />
          ),
          description: (
            <FormattedMessage
              id='deployment-overview.customer-engineer-callout-description.version-below-9'
              defaultMessage='<link> Connect with us </link> to get personalized help from {ce} on best practices, performance, and/or cost efficiency at no additional cost to you! '
              values={{
                link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
                ce: <strong> Customer Engineering </strong>,
              }}
            />
          ),
        }
      }

      return {
        // less than 60 days converted
        title: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-title.conversion-lt-60'
            defaultMessage='Need help getting started with Elastic? Engage Customer Engineering.'
          />
        ),
        description: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-description.conversion-lt-60'
            defaultMessage='<link> Connect with us </link> to get guided onboarding on best practices for architecture, data ingest, and/or cost efficiency at no additional cost to you!'
            values={{
              link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
            }}
          />
        ),
      }
    }

    // fallback to avoid gaps
    if (isTrialUser || (isConvertedUser && daysSinceTrialConverted === undefined)) {
      return {
        title: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-title.default'
            defaultMessage='Need advice? Engage a Customer Engineer.'
          />
        ),
        description: (
          <FormattedMessage
            id='deployment-overview.customer-engineer-callout-description.default'
            defaultMessage='<link> Connect with us </link> to get help with best practices, performance, and/or cost efficiency.'
            values={{
              link: (content) => <a href='https://www.elastic.co/contact/ce-help'>{content}</a>,
            }}
          />
        ),
      }
    }

    return undefined
  }

  const calloutContent = getCalloutContent()

  if (isCalloutVisible && calloutContent) {
    const { title, description } = calloutContent
    return (
      <EuiCallOut
        title={title}
        iconType='info'
        onDismiss={() => {
          setCalloutVisible(false)
        }}
      >
        <p>{description}</p>
      </EuiCallOut>
    )
  }

  return null
}

export default CustomerEngineerCallout
